// (B) message 스토어 — antd message 파리티 커스텀 host 의 상태 소스(sonner 미사용).
// realm(window)별 모듈 싱글톤(main/popup 각각 자기 번들 로드 → 자동 격리, notificationStack 과 동일 원리).
//
// antd message 구조 재현: 새 메시지는 배열 끝에 append(= 최신이 맨 아래), 각 항목은 open→closing 2단계로
// 닫혀서(자동 dismiss 또는 close) leave 애니메이션(collapse) 후 실제 제거된다. 타이머는 id 별로 관리하며
// close/제거 시 정리한다(cleanup 없는 setTimeout 금지 규칙 준수).
import type { ReactNode } from 'react'

export type OmMessageKind = 'success' | 'error' | 'info' | 'warning'

export interface OmMessageItem {
  id: number
  kind: OmMessageKind
  content: ReactNode
  // true 면 leave(collapse) 애니메이션 진행 중 → host 가 data-open=false 로 접는다.
  closing: boolean
}

export interface OmMessageAddOptions {
  // 표시 시간(초). null/undefined 면 기본값, 0 이하면 자동 닫힘 없음(수동 close 까지 유지).
  duration?: number
}

// antd message 기본 표시 시간(초) 파리티.
const DEFAULT_DURATION_SEC = 3
// leave(collapse) 애니메이션 시간(ms) — omMessage.css 의 transition 과 일치시켜야 한다.
const LEAVE_ANIM_MS = 300

let seq = 0
let items: OmMessageItem[] = []
const listeners = new Set<() => void>()
// id → 해당 항목의 활성 타이머 목록(자동 close, leave 후 제거). 정리 시 모두 clearTimeout.
const timers = new Map<number, ReturnType<typeof setTimeout>[]>()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function pushTimer(id: number, timer: ReturnType<typeof setTimeout>): void {
  const arr = timers.get(id) ?? []
  arr.push(timer)
  timers.set(id, arr)
}

function clearTimers(id: number): void {
  const arr = timers.get(id)
  if (arr != null) {
    arr.forEach(clearTimeout)
    timers.delete(id)
  }
}

export const messageStore = {
  // 새 메시지 추가 → 배열 끝에 append(최신이 맨 아래). 자동 닫힘 타이머 등록.
  add(kind: OmMessageKind, content: ReactNode, options?: OmMessageAddOptions): number {
    const id = (seq += 1)
    items = [...items, { id, kind, content, closing: false }]

    const durationSec = options?.duration ?? DEFAULT_DURATION_SEC
    if (durationSec > 0) {
      pushTimer(
        id,
        setTimeout(() => messageStore.close(id), durationSec * 1000),
      )
    }

    emit()
    return id
  },

  // 닫기 시작 → closing 표시(leave 애니메이션) 후 LEAVE_ANIM_MS 지나면 배열에서 제거.
  close(id: number): void {
    const target = items.find((item) => item.id === id)
    if (target == null || target.closing) return

    clearTimers(id) // 자동 닫힘 타이머 정리(중복 close 방지)
    items = items.map((item) => (item.id === id ? { ...item, closing: true } : item))
    pushTimer(
      id,
      setTimeout(() => {
        items = items.filter((item) => item.id !== id)
        clearTimers(id)
        emit()
      }, LEAVE_ANIM_MS),
    )
    emit()
  },

  getSnapshot(): OmMessageItem[] {
    return items
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}
